import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { EnvelopeSimple, Users, ChatCircleDots, Clock, ArrowRight } from "@phosphor-icons/react";

const INK = "#0A0A0A";
const BRAND = "#0047FF";
const ACCENT = "#FFD600";
const GOOD = "#16A34A";
const RISKY = "#F59E0B";
const BAD = "#DC2626";
const MUTED = "#9CA3AF";

const CATEGORY_COLORS = { lead: BRAND, support: RISKY, spam: BAD };
const QUALITY_COLORS  = { good: GOOD, risky: RISKY, bad: BAD };
const ROUTE_COLORS    = { sales: BRAND, support: RISKY, nurture: ACCENT, reject: BAD };

export default function Analytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.get("/analytics/overview", { params: { days } }).then((r) => setData(r.data));
  }, [days]);

  if (!data) {
    return (
      <DashboardLayout title="Analytics">
        <div className="cell-strong p-12 bg-white text-center text-[var(--ink-soft)]">Loading insights…</div>
      </DashboardLayout>
    );
  }

  const k = data.kpis;
  return (
    <DashboardLayout
      title="Analytics"
      actions={<RangePicker value={days} onChange={setDays} />}
    >
      {/* KPI row */}
      <div className="grid grid-cols-12 gap-4">
        <Kpi className="col-span-12 md:col-span-3" icon={EnvelopeSimple} label="Emails processed" value={k.emails_processed} hint={`of ${k.emails_total} received`} />
        <Kpi className="col-span-12 md:col-span-3" icon={Users} label="Leads scored" value={k.leads_processed} hint={`${k.leads_good_pct}% marked good`} accent />
        <Kpi className="col-span-12 md:col-span-3" icon={ChatCircleDots} label="Chats handled" value={k.chats_total} hint="across all sessions" />
        <Kpi className="col-span-12 md:col-span-3" icon={Clock} label="Hours saved" value={`${k.hours_saved}h`} hint="estimated" dark />

        {/* Timeline */}
        <div className="col-span-12 cell-strong bg-white p-6">
          <ChartHeader
            tag="/ Activity"
            title={`Last ${days} days · daily volume`}
            sub="Emails received · Leads received · Chats handled. Hover for the day's breakdown."
          />
          <div className="h-[280px] mt-4" data-testid="chart-timeline">
            <ResponsiveContainer>
              <AreaChart data={data.timeline} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="g-emails" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BRAND} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={BRAND} stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="g-leads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={ACCENT} stopOpacity={0.7} />
                    <stop offset="100%" stopColor={ACCENT} stopOpacity={0.1} />
                  </linearGradient>
                  <linearGradient id="g-chats" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={INK} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={INK} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: MUTED, fontFamily: "ui-monospace, monospace" }}
                       tickFormatter={shortDate} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
                <YAxis tick={{ fontSize: 10, fill: MUTED, fontFamily: "ui-monospace, monospace" }}
                       tickLine={false} axisLine={false} allowDecimals={false} width={32} />
                <Tooltip content={<RcTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "ui-monospace, monospace", textTransform: "uppercase", letterSpacing: "0.08em" }} />
                <Area type="monotone" dataKey="emails" name="Emails" stroke={BRAND} strokeWidth={2} fill="url(#g-emails)" />
                <Area type="monotone" dataKey="leads"  name="Leads"  stroke={INK}    strokeWidth={2} fill="url(#g-leads)" />
                <Area type="monotone" dataKey="chats"  name="Chats"  stroke={MUTED}  strokeWidth={2} fill="url(#g-chats)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Email category donut */}
        <div className="col-span-12 lg:col-span-4 cell-strong bg-white p-6">
          <ChartHeader tag="/ Inbox" title="Email categories" sub="What your AI sorted." />
          <DistroDonut testId="chart-email-categories" data={data.email_categories} colors={CATEGORY_COLORS} />
        </div>

        {/* Lead quality donut */}
        <div className="col-span-12 lg:col-span-4 cell-strong bg-white p-6">
          <ChartHeader tag="/ Pipeline" title="Lead quality mix" sub="Good vs risky vs bad." />
          <DistroDonut testId="chart-lead-quality" data={data.lead_quality} colors={QUALITY_COLORS} />
          {k.avg_lead_score != null && (
            <div className="mt-3 flex items-baseline justify-between text-xs cell p-2 bg-[var(--bg-soft)]">
              <span className="overline text-[var(--ink-soft)]">Avg score</span>
              <span className="font-display text-2xl">{k.avg_lead_score}</span>
            </div>
          )}
        </div>

        {/* Lead routes */}
        <div
          className="col-span-12 lg:col-span-4 p-6"
          style={{ background: INK, color: "#FFFFFF", border: `1px solid ${INK}` }}
        >
          <ChartHeader tag="/ Routing" title="Where leads went" sub="Where your AI sent them." dark />
          <BarRows
            testId="chart-lead-routes"
            data={data.lead_routes}
            colorMap={ROUTE_COLORS}
            dark
          />
        </div>

        {/* Top senders */}
        <div className="col-span-12 lg:col-span-7 cell-strong bg-white p-6">
          <ChartHeader tag="/ Inbox" title="Top senders" sub="Where most of your inbound comes from." />
          <div className="h-[260px] mt-3" data-testid="chart-top-senders">
            <ResponsiveContainer>
              <BarChart data={data.top_senders.slice().reverse()} layout="vertical" margin={{ left: 80, right: 12 }}>
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="2 4" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: MUTED, fontFamily: "ui-monospace, monospace" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: INK }} tickLine={false} axisLine={false} width={180}
                       tickFormatter={(v) => v.length > 24 ? v.slice(0, 22) + "…" : v} />
                <Tooltip content={<RcTooltip />} />
                <Bar dataKey="value" name="Emails" fill={BRAND} barSize={20} radius={[0,2,2,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Lead sources */}
        <div className="col-span-12 lg:col-span-5 cell-strong p-6" style={{ background: ACCENT, border: "1px solid #0A0A0A" }}>
          <ChartHeader tag="/ Acquisition" title="Lead sources" sub="Where leads are arriving from." />
          <BarRows testId="chart-lead-sources" data={data.lead_sources} solidColor={INK} />
        </div>

        {/* CTA */}
        <div className="col-span-12 cell-strong bg-white p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="overline text-[var(--ink-soft)]">/ Hint</div>
            <div className="font-display text-2xl mt-1">Numbers stale? Process more.</div>
            <p className="text-sm text-[var(--ink-soft)] mt-1">Run a triage or score round to see your AI employee make a dent.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/dashboard/email" className="btn-secondary text-sm" data-testid="analytics-cta-email">Triage emails <ArrowRight size={16} weight="bold" /></Link>
            <Link to="/dashboard/leads" className="btn-primary text-sm" data-testid="analytics-cta-leads">Score leads <ArrowRight size={16} weight="bold" /></Link>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function shortDate(s) {
  if (!s) return "";
  const [, m, d] = s.split("-");
  return `${m}/${d}`;
}

function ChartHeader({ tag, title, sub, dark }) {
  return (
    <div>
      <div className={`overline ${dark ? "text-white/55" : "text-[var(--ink-soft)]"}`}>{tag}</div>
      <div className={`font-display text-2xl mt-1 ${dark ? "text-white" : ""}`}>{title}</div>
      {sub && <div className={`text-sm mt-1 ${dark ? "text-white/60" : "text-[var(--ink-soft)]"}`}>{sub}</div>}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint, accent, dark, className = "" }) {
  const style = dark
    ? { background: INK, color: "#FFFFFF", border: `1px solid ${INK}` }
    : accent
    ? { background: ACCENT, border: `1px solid ${INK}` }
    : { background: "#FFFFFF", border: `1px solid ${INK}` };
  return (
    <div
      className={`p-5 ${className}`}
      style={style}
      data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center justify-between">
        <div className="overline" style={{ color: dark ? "rgba(255,255,255,0.6)" : "#4B5563" }}>{label}</div>
        <Icon size={18} weight="duotone" color={dark ? "#FFFFFF" : INK} />
      </div>
      <div className="font-display text-4xl mt-2" style={{ color: dark ? "#FFFFFF" : INK }}>{value}</div>
      {hint && (
        <div className="text-xs mt-1" style={{ color: dark ? "rgba(255,255,255,0.6)" : "#4B5563" }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function DistroDonut({ data, colors, testId }) {
  const filtered = (data || []).filter((d) => d.value > 0);
  const total = filtered.reduce((s, d) => s + d.value, 0);

  if (!total) {
    return <div className="h-[200px] mt-3 flex items-center justify-center text-sm text-[var(--ink-soft)]" data-testid={testId}>
      No data yet — run a triage from the module.
    </div>;
  }
  return (
    <div className="mt-3" data-testid={testId}>
      <div className="h-[180px]">
        <ResponsiveContainer>
          <PieChart>
            <Tooltip content={<RcTooltip />} />
            <Pie data={filtered} dataKey="value" nameKey="name" innerRadius={48} outerRadius={75}
                 paddingAngle={2} stroke={INK} strokeWidth={1}>
              {filtered.map((d, i) => (
                <Cell key={i} fill={colors[d.name] || BRAND} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="space-y-1.5 mt-3 text-sm">
        {filtered.map((d) => (
          <li key={d.name} className="flex items-center gap-2">
            <span className="w-3 h-3 inline-block" style={{ background: colors[d.name] || BRAND, border: `1px solid ${INK}` }} />
            <span className="capitalize">{d.name}</span>
            <span className="ml-auto font-mono-rc text-xs">
              {d.value} <span className="text-[var(--ink-soft)]">· {Math.round((d.value / total) * 100)}%</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BarRows({ data, colorMap, solidColor, dark, testId }) {
  if (!data || data.length === 0) {
    return <div className={`mt-4 text-sm ${dark ? "text-white/60" : "text-[var(--ink-soft)]"}`} data-testid={testId}>No data yet.</div>;
  }
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <ul className="mt-3 space-y-2" data-testid={testId}>
      {data.map((d) => {
        const pct = (d.value / max) * 100;
        const fill = solidColor || (colorMap && (colorMap[d.name] || BRAND)) || BRAND;
        return (
          <li key={d.name}>
            <div className="flex items-baseline justify-between text-xs mb-1">
              <span className={`capitalize ${dark ? "text-white" : ""}`}>{d.name}</span>
              <span className={`font-mono-rc ${dark ? "text-white/70" : "text-[var(--ink-soft)]"}`}>{d.value}</span>
            </div>
            <div className={`h-2 ${dark ? "bg-white/10" : "bg-[var(--bg-soft)]"} border ${dark ? "border-white/20" : "border-[var(--ink)]"}`}>
              <div className="h-full transition-all" style={{ width: `${pct}%`, background: fill }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function RangePicker({ value, onChange }) {
  return (
    <div className="inline-flex border border-[var(--ink)] bg-white" data-testid="analytics-range-picker">
      {[7, 30, 90].map((d) => (
        <button
          key={d}
          onClick={() => onChange(d)}
          className={`px-3 py-2 text-xs uppercase tracking-wide font-mono-rc ${value === d ? "bg-[var(--ink)] text-white" : "text-[var(--ink)] hover:bg-[var(--bg-soft)]"}`}
          data-testid={`range-${d}`}
        >
          {d}d
        </button>
      ))}
    </div>
  );
}

function RcTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="px-3 py-2 text-xs font-mono-rc shadow-lg"
      style={{ background: INK, color: "#FFFFFF", border: `1px solid ${INK}` }}
    >
      {label && <div style={{ color: "rgba(255,255,255,0.6)" }} className="mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 inline-block" style={{ background: p.color || p.payload.fill }} />
          <span>{p.name || p.payload.name}</span>
          <span className="ml-auto">{p.value}</span>
        </div>
      ))}
    </div>
  );
}
